<template>

  <Head>
    <title>{{ title }}</title>
    <meta name="description" :content="description" />
  </Head>

  <main class="home-page">
    <section class="hero">
      <div class="hero__food" :style="{ backgroundImage: `url(${heroFish})` }"></div>
      <div class="hero__overlay"></div>
      <div class="hero__texture"></div>

      <div class="page-shell hero__content">
        <div class="hero__copy-wrap">
          <p class="eyebrow">The Island House</p>
          <h1>Fish &amp; Chips<br /><span>Done Properly.</span></h1>
          <p class="hero__copy">
            Freshly prepared. Quality ingredients.<br />
            100% cod. Serving the Black Country<br class="desktop-only" /> for over 40 years.
          </p>

          <div class="hero__actions">
            <router-link to="/order" class="brand-button">Order Online <span>→</span></router-link>
            <router-link to="/our-food" class="outline-button">View Menu</router-link>
          </div>

          <div class="hero__proof">
            <div class="proof-item">
              <span class="proof-icon" v-html="icons.fish"></span>
              <span><strong>100% Cod</strong><small>Sustainably sourced<br />and hand filleted</small></span>
            </div>
            <div class="proof-item">
              <span class="proof-icon" v-html="icons.clock"></span>
              <span><strong>Cooked Fresh</strong><small>Every order cooked<br />to perfection</small></span>
            </div>
            <div class="proof-item">
              <span class="proof-icon" v-html="icons.badge"></span>
              <span><strong>40+ Years</strong><small>Proudly serving our<br />community</small></span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="location-strip section-light">
      <div class="page-shell compact-shell">
        <div class="section-heading section-heading--dark">
          <p class="eyebrow">Choose your nearest</p>
          <h2>Island House</h2>
          <p>Select your nearest location to start your order for collection or delivery.</p>
        </div>

        <div class="quick-locations">
          <article v-for="location in featuredLocations" :key="location.name" class="quick-location-card">
            <span class="location-mark" v-html="icons.shop"></span>
            <h3>{{ location.name }}</h3>
            <p>{{ location.address }}</p>
            <a :href="orderLink(location)" target="_blank" rel="noopener" class="location-order">Order Now</a>
          </article>
        </div>

        <router-link to="/locations" class="text-link">View all locations →</router-link>
      </div>
    </section>

    <section class="favourites">
      <div class="page-shell compact-shell">
        <div class="section-heading">
          <p class="eyebrow">Our favourites</p>
          <h2>Loved by our customers</h2>
        </div>

        <div class="food-grid">
          <article v-for="item in favourites" :key="item.name" class="food-card">
            <img :src="item.image" :alt="item.name" />
            <div class="food-card__body">
              <h3>{{ item.name }}</h3>
              <p>{{ item.copy }}</p>
              <router-link to="/order">Order now →</router-link>
            </div>
          </article>
        </div>

        <div class="center">
          <router-link to="/our-food" class="menu-link">View Full Menu →</router-link>
        </div>
      </div>
    </section>

    <section class="section-light why-us">
      <div class="page-shell compact-shell">
        <div class="section-heading section-heading--dark">
          <p class="eyebrow">Why choose</p>
          <h2>The Island House</h2>
        </div>

        <div class="benefits-grid">
          <div v-for="benefit in benefits" :key="benefit.title" class="benefit">
            <span class="benefit__icon" v-html="icons[benefit.icon]"></span>
            <h3>{{ benefit.title }}</h3>
            <p>{{ benefit.copy }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="story-section">
      <div class="page-shell compact-shell">
        <div class="story-card">
          <div class="story-card__image-wrap">
            <img :src="storyImage" alt="The Island House team" class="story-card__image" />
          </div>
          <div class="story-card__content">
            <p class="eyebrow">Our story</p>
            <h2>A Tradition of Quality</h2>
            <p>
              Established over 40 years ago, The Island House has become a much-loved part of the community.
              We are passionate about traditional fish and chips, generous portions and food cooked properly.
            </p>
            <router-link to="/our-food" class="brand-button story-button">Read Our Story <span>→</span></router-link>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { Head } from '@vueuse/head'
import heroFish from '../assets/img/home-redesign/hero-fish.webp'
import fishChips from '../assets/img/home-redesign/fish-chips.webp'
import donerKebab from '../assets/img/home-redesign/doner-kebab.webp'
import loadedChips from '../assets/img/home-redesign/loaded-chips.webp'
import southernFriedChicken from '../assets/img/home-redesign/southern-fried-chicken.webp'
import grilledFavourites from '../assets/img/home-redesign/grilled-favourites.webp'
import storyImage from '../assets/img/home-redesign/story-team.webp'

export default {
  components: { Head },
  data() {
    return {
      title: 'The Island House | Fish & Chips Done Properly',
      description: 'Proper fish and chips, freshly cooked across multiple Island House locations.',
      heroFish,
      storyImage,
      icons: {
        fish: `<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M3 16c5-6 11-9 18-7l5-4v8l3 3-3 3v8l-5-4c-7 2-13-1-18-7Z"/><circle cx="20" cy="13" r="1.2"/></svg>`,
        clock: `<svg viewBox="0 0 32 32" aria-hidden="true"><circle cx="16" cy="16" r="12"/><path d="M16 9v8l5 3"/></svg>`,
        badge: `<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M16 3l3 3.2 4.4-.4.9 4.3 3.8 2.2-2.2 3.8.4 4.4-4.3.9-2.2 3.8-3.8-2.2-3.8 2.2-2.2-3.8-4.3-.9.4-4.4-2.2-3.8 3.8-2.2.9-4.3 4.4.4L16 3Z"/><path d="m16 10 1.8 3.6 4 .6-2.9 2.8.7 4-3.6-1.9-3.6 1.9.7-4-2.9-2.8 4-.6L16 10Z"/></svg>`,
        shop: `<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M6 14h20v14H6zM4 14l3-7h18l3 7M10 7V4h12v3M12 28v-8h8v8"/></svg>`,
        fishSolid: `<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M3 16c5-6 11-9 18-7l5-4v8l3 3-3 3v8l-5-4c-7 2-13-1-18-7Z"/><circle cx="20" cy="13" r="1.2"/></svg>`,
        award: `<svg viewBox="0 0 32 32" aria-hidden="true"><circle cx="16" cy="13" r="8"/><path d="m11 20-2 9 7-4 7 4-2-9M16 8l1.5 3 3.5.5-2.5 2.4.6 3.4-3.1-1.6-3.1 1.6.6-3.4-2.5-2.4 3.5-.5L16 8Z"/></svg>`,
        sparkle: `<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M16 3c1.4 7.2 5.8 11.6 13 13-7.2 1.4-11.6 5.8-13 13-1.4-7.2-5.8-11.6-13-13C10.2 14.6 14.6 10.2 16 3Z"/></svg>`,
        chef: `<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M9 16a6 6 0 0 1 2-11 6 6 0 0 1 10 0 6 6 0 0 1 2 11v9H9v-9ZM9 20h14M12 25v4h8v-4"/></svg>`
      },
      favourites: [
        { name: 'Fish & Chips', copy: 'Crisp batter, flaky cod and golden chips.', image: fishChips },
        { name: 'Doner Kebab', copy: 'Loaded with fresh salad and your favourite sauce.', image: donerKebab },
        { name: 'Loaded Chips', copy: 'Our chips, your way. Fully loaded.', image: loadedChips },
        { name: 'Southern Fried Chicken', copy: 'Crispy, seasoned and cooked fresh.', image: southernFriedChicken },
        { name: 'Grilled Favourites', copy: 'Freshly prepared and full of flavour.', image: grilledFavourites }
      ],
      benefits: [
        { icon: 'fishSolid', title: '100% Cod', copy: 'We only use quality fish. No fillers, no compromise.' },
        { icon: 'award', title: '40+ Years Experience', copy: 'Proudly serving local communities for decades.' },
        { icon: 'sparkle', title: 'Quality Ingredients', copy: 'We use the best ingredients and quality products in everything we do.' },
        { icon: 'chef', title: 'Cooked Fresh', copy: 'Freshly prepared to order, every single time.' }
      ]
    }
  },
  computed: {
    featuredLocations() {
      return this.allLocations.slice(0, 3)
    },
    allLocations() {
      const s = this.$store.state
      return [
        { name: s.tiptonName, address: s.tiptonAddress, businessAttribute: s.tiptonBusinessAttribute },
        { name: s.staffordRdName, address: s.staffordRdAddress, businessAttribute: s.staffordRdBusinessAttribute },
        { name: s.newbridgeName, address: s.newbridgeAddress, businessAttribute: s.newbridgeBusinessAttribute },
        { name: s.edgbastonName, address: s.edgbastonAddress, businessAttribute: s.edgbastonBusinessAttribute }
      ]
    }
  },
  methods: {
    orderLink(location) {
      return `https://app.orderopia.com/${location.businessAttribute}/order`
    }
  }
}
</script>

<style scoped>
.home-page {
  background: #080808
}

.compact-shell {
  width: min(1080px, calc(100% - 48px))
}

.eyebrow {
  text-transform: uppercase;
  color: var(--primary);
  font-size: 11px !important;
  font-weight: 900;
  letter-spacing: .13em;
  margin-bottom: 7px !important
}

.section-heading {
  text-align: center
}

.section-heading h2 {
  font-size: 35px;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: -.025em
}

.section-heading>p:last-child {
  font-size: 12px !important;
  margin-top: 10px !important;
  color: #9c9c9c
}

.section-heading--dark>p:last-child {
  color: #78736b
}

.hero {
  height: 640px;
  position: relative;
  overflow: hidden;
  background: #060606;
  display: flex;
  align-items: center
}

.hero__food {
  position: absolute;
  right: 0;
  top: 0;
  width: 66%;
  height: 100%;
  background-size: cover;
  background-position: center center
}

.hero__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #070707 0%, #070707 28%, rgba(7, 7, 7, .89) 39%, rgba(7, 7, 7, .28) 67%, rgba(7, 7, 7, .05) 100%), linear-gradient(0deg, rgba(4, 4, 4, .35), transparent 44%)
}

.hero__texture {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 14% 50%, rgba(255, 255, 255, .035), transparent 30%);
  mix-blend-mode: screen;
  pointer-events: none
}

.hero__content {
  position: relative;
  z-index: 2;
  padding-top: 72px
}

.hero__copy-wrap {
  max-width: 610px
}

.hero h1 {
  text-transform: uppercase;
  font-size: 68px;
  line-height: .9;
  letter-spacing: -.035em;
  margin: 0;
  color: #fff;
  font-weight: 950
}

.hero h1 span {
  color: var(--primary)
}

.hero__copy {
  font-size: 14px !important;
  color: #e0e0e0;
  line-height: 1.6;
  margin: 20px 0 25px !important
}

.hero__actions {
  display: flex;
  gap: 12px
}

.hero__actions .brand-button,
.hero__actions .outline-button {
  min-width: 148px;
  height: 46px
}

.hero__proof {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 42px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, .18);
  max-width: 610px
}

.proof-item {
  display: flex;
  align-items: flex-start;
  gap: 11px
}

.proof-icon {
  width: 35px;
  height: 35px;
  border: 2px solid var(--primary);
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: var(--primary);
  font-size: 17px;
  flex: 0 0 35px
}

.proof-item strong {
  display: block;
  text-transform: uppercase;
  font-size: 10px;
  line-height: 1.2
}

.proof-item small {
  display: block;
  color: #aaa;
  font-size: 9px;
  line-height: 1.4;
  margin-top: 4px
}

.location-strip {
  padding: 48px 24px 45px
}

.quick-locations {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 27px auto 14px
}

.quick-location-card {
  background: #fff;
  border: 1px solid #d9d4ca;
  padding: 20px 22px 19px;
  text-align: center;
  color: #101010;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center
}

.location-mark {
  color: var(--primary);
  font-size: 28px;
  line-height: 1
}

.quick-location-card h3 {
  text-transform: uppercase;
  font-size: 19px;
  margin: 6px 0 6px
}

.quick-location-card p {
  color: #69645d;
  font-size: 10px !important;
  line-height: 1.45;
  margin-bottom: 15px !important;
  min-height: 30px
}

.location-order {
  margin-top: auto;
  background: var(--primary);
  color: #0c0c0c;
  text-transform: uppercase;
  font-size: 10px;
  font-weight: 900;
  width: 100%;
  padding: 9px 12px
}

.location-order:hover {
  color: #0c0c0c;
  background: #e0a33b
}

.text-link {
  display: block;
  width: max-content;
  margin: 0 auto;
  color: #111;
  font-size: 10px;
  font-weight: 900;
  text-decoration: none
}

.favourites {
  background: #0a0a0a;
  padding: 52px 24px 48px
}

.food-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 7px;
  margin: 25px 0 19px
}

.food-card {
  background: #101010;
  border: 1px solid #373737
}

.food-card img {
  display: block;
  width: 100%;
  height: 145px;
  object-fit: cover
}

.food-card__body {
  padding: 11px 12px 13px
}

.food-card h3 {
  text-transform: uppercase;
  font-size: 12px;
  line-height: 1.15;
  min-height: 28px
}

.food-card p {
  font-size: 9px !important;
  color: #aaa;
  line-height: 1.45;
  min-height: 40px;
  margin: 5px 0 10px !important
}

.food-card a {
  color: var(--primary);
  font-size: 9px;
  font-weight: 900;
  text-transform: uppercase
}

.center {
  text-align: center
}

.menu-link {
  display: inline-flex;
  justify-content: center;
  min-width: 230px;
  border: 1px solid var(--primary);
  color: var(--primary);
  text-transform: uppercase;
  font-size: 10px;
  font-weight: 900;
  padding: 10px 18px
}

.menu-link:hover {
  background: var(--primary);
  color: #111
}

.why-us {
  padding: 43px 24px 46px
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin-top: 25px
}

.benefit {
  text-align: center;
  padding: 0 30px;
  border-right: 1px solid #d7d1c7
}

.benefit:last-child {
  border-right: 0
}

.benefit__icon {
  display: block;
  font-size: 24px;
  line-height: 1;
  color: var(--primary)
}

.benefit h3 {
  text-transform: uppercase;
  font-size: 10px;
  margin: 9px 0 5px
}

.benefit p {
  font-size: 9px !important;
  color: #666;
  line-height: 1.45
}

.story-section {
  background: #111;
  padding: 36px 24px 42px
}

.story-card {
  display: grid;
  grid-template-columns: 43% 57%;
  background: #080808;
  border: 1px solid #353535;
  overflow: hidden;
  min-height: 245px
}

.story-card__image-wrap {
  padding: 17px 0 17px 17px;
  display: flex;
  align-items: center
}

.story-card__image {
  width: 100%;
  height: 210px;
  object-fit: cover;
  filter: grayscale(1);
  transform: rotate(-1.2deg);
  border: 7px solid #e7e1d7;
  box-shadow: 0 12px 24px rgba(0, 0, 0, .35)
}

.story-card__content {
  padding: 40px 46px 32px
}

.story-card__content h2 {
  text-transform: uppercase;
  font-size: 27px;
  line-height: 1;
  margin-bottom: 15px
}

.story-card__content>p:not(.eyebrow) {
  color: #c4c4c4;
  font-size: 11px !important;
  line-height: 1.7;
  max-width: 540px;
  margin-bottom: 19px !important
}

.story-button {
  height: 39px;
  font-size: 9px;
  padding: 0 17px
}

@media(max-width:1000px) {
  .hero {
    height: 620px
  }

  .hero__food {
    width: 74%
  }

  .hero__overlay {
    background: linear-gradient(90deg, #070707 0%, #070707 25%, rgba(7, 7, 7, .86) 45%, rgba(7, 7, 7, .22) 78%)
  }

  .hero h1 {
    font-size: 58px
  }

  .food-card img {
    height: 125px
  }

  .story-card__content {
    padding: 35px 34px
  }

  .compact-shell {
    width: min(930px, calc(100% - 40px))
  }
}

@media(max-width:760px) {
  .compact-shell {
    width: calc(100% - 28px)
  }

  .hero {
    height: auto;
    min-height: 720px;
    align-items: flex-end;
    padding-bottom: 35px
  }

  .hero__food {
    inset: 0;
    width: 100%;
    height: 56%;
    background-position: 58% center
  }

  .hero__overlay {
    background: linear-gradient(180deg, rgba(7, 7, 7, .2) 0%, rgba(7, 7, 7, .22) 28%, #070707 57%, #070707 100%)
  }

  .hero__content {
    padding-top: 300px
  }

  .hero__copy-wrap {
    max-width: none
  }

  .hero h1 {
    font-size: 48px
  }

  .hero__copy {
    font-size: 13px !important
  }

  .hero__proof {
    gap: 10px
  }

  .proof-item {
    display: block
  }

  .proof-icon {
    margin-bottom: 8px
  }

  .proof-item small {
    font-size: 8px
  }

  .quick-locations {
    grid-template-columns: 1fr
  }

  .quick-location-card {
    min-height: 0
  }

  .food-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px
  }

  .food-card img {
    height: 150px
  }

  .food-card:last-child {
    grid-column: 1 / -1
  }

  .benefits-grid {
    grid-template-columns: 1fr 1fr;
    gap: 28px 0
  }

  .benefit {
    border-right: 0;
    padding: 0 15px
  }

  .story-card {
    grid-template-columns: 1fr
  }

  .story-card__image-wrap {
    padding: 18px
  }

  .story-card__content {
    padding: 24px
  }

  .story-card__image {
    height: 220px
  }

  .section-heading h2 {
    font-size: 29px
  }
}

@media(max-width:430px) {
  .desktop-only {
    display: none
  }

  .hero h1 {
    font-size: 42px
  }

  .hero__actions {
    flex-direction: column;
    align-items: flex-start
  }

  .hero__actions .brand-button,
  .hero__actions .outline-button {
    width: 170px
  }

  .hero__proof {
    grid-template-columns: repeat(3, 1fr)
  }

  .food-grid {
    grid-template-columns: 1fr 1fr
  }

  .food-card img {
    height: 125px
  }

  .story-card__image {
    height: 180px
  }
}
</style>

<style scoped>
.proof-icon :deep(svg){width:28px;height:28px;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}
.location-mark :deep(svg){width:27px;height:27px;fill:none;stroke:currentColor;stroke-width:1.7;stroke-linecap:round;stroke-linejoin:round}
.benefit__icon :deep(svg){width:34px;height:34px;fill:none;stroke:currentColor;stroke-width:1.7;stroke-linecap:round;stroke-linejoin:round}
</style>
